'use client';

import { PromotionStar as PromotionStarIcon } from '@ahafood/icons';
import { Button } from '@ahafood/ui/components/button';
import { Image } from '@ahafood/ui/components/image';
import { Skeleton } from '@ahafood/ui/components/skeleton';
import { assetPrefix } from '@ahm/api-wrappers-core';
import { cn } from '@ahm/common-helpers';
import { motion } from 'framer-motion';
import { useSearchParams, useRouter } from 'next/navigation';
import { useMemo } from 'react';
import type { Tag } from '@/api/ahafood/get-deal-fn';
import { processDealData } from '@/api/utils';
import ImageWithFallback from '@/app/[locale]/(main)/store/components/items/fallback-image';
import FreeModeSwiper from '@/app/[locale]/(main)/store/components/items/free-mode-swiper';
import { trackEvent } from '@/lib/event-tracking';
import { useIsMobile } from '@/lib/is-mobile/use-is-mobile';
import { useAiFoodOrderStore, useAuthStore } from '@/store';

type OldDealProps = {
  deals: Tag[];
  isLoading?: boolean;
  city_slug?: string;
  payload: {
    city_id: string;
    category_slug?: string;
    promotion?: boolean;
    // freeship?: boolean;
    special_request?: string;
    hot_deal?: boolean;
    campaign_id?: string;
  };
};

export default function NewOldStoreDeal({
  deals,
  isLoading,
  payload,
  city_slug,
}: OldDealProps) {
  const isMobile = useIsMobile();
  const searchParams = useSearchParams();
  const router = useRouter();
  const { user } = useAuthStore();

  const cityId = useAiFoodOrderStore.use.city_id();
  const deal = payload.campaign_id
    ? parseInt(payload.campaign_id, 10)
    : parseFloat(searchParams.get('campaign_id')!);

  const processedDeals = useMemo(() => {
    if (!deals) return { deal: { tags: [], title: '' } };
    return processDealData(deals);
  }, [deals]);

  function handleDealToggle(d: number) {
    router.push(`/${city_slug}/danh-sach-cua-hang-co-campaign-${d}`);
    trackEvent('click_button', {
      action_time: Math.floor(Date.now() / 1000),
      button_name: 'super_deal_toggle',
      timestamp: Date.now(),
      deal_id: d,
      user_id: user?.cunstomerId,
    });
  }

  // function handleViewAll() {
  //   if (processedDeals.deal.tags.length > 0) {
  //     handleDealToggle(processedDeals.deal.tags[0]._id);
  //   }
  // }

  if (isLoading ?? !cityId) {
    return (
      <div className="flex flex-row flex-nowrap gap-4">
        {[...Array(isMobile ? 2 : 3)].map((_, index) => (
          <Skeleton
            key={`old-deal-skeleton-${index}`}
            className="h-[272px] w-48 flex-none rounded-3xl bg-neutral-10/75 md:h-[320px] md:w-60"
          />
        ))}
      </div>
    );
  }

  if (!processedDeals.deal || processedDeals.deal.tags.length === 0)
    return null;

  return (
    <div
      className="rounded-2xl overflow-hidden bg-gradient-to-b from-[#FFFEF3] to-[#FFEDBF] shadow-sm p-px"
      data-testid="home-super-deal-section"
    >
      <div
        className="relative p-4 md:p-6 pb-6 rounded-2xl bg-[#FFFEF3] gap-6 flex flex-col"
        style={{ boxShadow: '0px 4px 4px 1px #0000001F inset' }}
      >
        <div className="relative z-10 flex items-center justify-between">
          <motion.div
            // initial={{ opacity: 0, x: -20 }}
            // animate={{ opacity: 1, x: 0 }}
            // transition={{ duration: 0.6, ease: 'easeOut' }}
            className="relative z-[5] flex w-full justify-between"
          >
            <Image
              src={'/static/icons/super-deal.svg'}
              alt="super-deal-title"
              width={180}
              height={31}
              className="object-contain object-bottom"
            />
            {/* <Button
              size="icon"
              variant="link"
              onClick={handleViewAll}
              className="flex items-center size-7 text-xl font-bold text-white bg-[#9B6F00] hover:bg-[#9B6F00] rounded-full [&>svg]:size-5 [&>svg]:font-bold"
            >
              <ArrowRight />
            </Button> */}
          </motion.div>
        </div>
        {/* <div className="space-y-6"> */}

        {/* <motion.h2
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.6, ease: 'easeOut' }}
        className="relative z-[5] flex items-center gap-2 md:gap-3"
      >
        <span className="text-4xl md:text-6xl drop-shadow-lg">🌟</span>
        <span className="text-2xl md:text-5xl font-black bg-gradient-to-r from-amber-600 via-yellow-500 to-orange-500 bg-clip-text text-transparent drop-shadow-sm tracking-tight">
          SUPER DEAL
        </span>
      </motion.h2> */}

        <FreeModeSwiper
          navigation={false}
          className="w-full !overflow-visible"
          spaceBetween={16}
          pagination
        >
          {processedDeals.deal.tags.map((d, index) => (
            <FreeModeSwiper.Slide
              data-testid="home-super-deal-card"
              key={`deal-${d._id}-${index}`}
              style={{ width: isMobile ? 192 : 240, aspectRatio: '240/320' }}
            >
              <motion.div
                custom={index}
                initial="hidden"
                animate="visible"
                whileHover="hover"
                whileTap="tap"
                className="h-full shadow-4 rounded-2xl"
                data-testid="home-super-deal-card"
              >
                <Button
                  variant="ghost"
                  onClick={() => {
                    handleDealToggle(d._id);
                  }}
                  className="group relative flex h-full w-48 flex-col justify-start overflow-hidden rounded-2xl bg-white p-0 md:w-60 border-none gap-0"
                >
                  <div
                    className={cn(
                      'absolute z-[3] size-full rounded-3xl bg-transparent transition-all duration-300',
                      {
                        'ring-4 ring-primary-50 ring-offset-2': d._id === deal,
                      }
                    )}
                  />

                  <div className="relative aspect-square h-auto w-full overflow-hidden">
                    <ImageWithFallback
                      src={
                        d.icon_image_url ??
                        `${assetPrefix}/static/images/empty-store.webp`
                      }
                      alt={d.description_vi_vn}
                      fill
                      style={{ objectFit: 'cover' }}
                      fallbackSrc={`${assetPrefix}/static/images/empty-store.webp`}
                      // className="transition-transform duration-700 ease-out group-hover:scale-110"
                    />
                  </div>

                  <div className="flex w-full flex-col flex-wrap items-center gap-2 px-4 pb-4 pt-3 text-xs font-medium capitalize text-neutral-50 relative z-[1]">
                    <h3 className="w-full truncate text-sm font-bold text-neutral-70 group-hover:line-clamp-2 group-hover:whitespace-pre-line group-hover:text-neutral-90 md:text-base tracking-tight">
                      {d.title_vi_vn}
                    </h3>
                    <div className="flex w-full flex-nowrap items-center justify-center truncate">
                      <PromotionStarIcon className="mr-1 size-5 md:size-6 text-amber-500" />
                      <span className="truncate text-xs font-medium md:text-sm text-neutral-60">
                        {d.description_vi_vn}
                      </span>
                    </div>
                  </div>
                </Button>
              </motion.div>
            </FreeModeSwiper.Slide>
          ))}
        </FreeModeSwiper>

        {/* View All Button */}
        {/* <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
        >
          <Button
            variant="outline"
            style={{ boxShadow: '0px 4px 4px 1px #0000001F inset' }}
            onClick={handleViewAll}
            className="w-full h-12 md:h-14 bg-[#FFEDBF] text-[#9B6F00] font-bold text-sm md:text-base rounded-2xl border-[#FFDA7D]"
          >
            Xem tất cả Super Deal
            <ArrowRight className="ml-2 size-4 group-hover:translate-x-1 transition-transform" />
          </Button>
        </motion.div> */}
      </div>
    </div>
  );
}
